import {Icon} from '@ezcater/tapas';
import {faMagnifyingGlass} from '@fortawesome/pro-regular-svg-icons';
import useTranslation from 'next-translate/useTranslation';
import {twJoin} from 'tailwind-merge';

import useEventBarFastFollows from '@/components/global/layout/AppBar/hooks/useEventBarFastFollows';
import {useEventBarV2} from './EventBarV2Provider';

type EventBarV2CollapsedTriggerProps = {
  onExpand: () => void;
};

// FDIC collapsed stand-in for EventBarV2: a single search pill. AppBar sizes this to the real
// bar (absolute inset-0 over an in-layout EventBarV2) so collapsed and expanded share a width.
const EventBarV2CollapsedTrigger: React.FC<EventBarV2CollapsedTriggerProps> = ({onExpand}) => {
  const {t} = useTranslation('app-bar');
  const {whatValue} = useEventBarV2();
  const {showEventBarFastFollows} = useEventBarFastFollows();

  return (
    <button
      // The treatment labels the pill "Search", so the accessible name has to lead with it
      // (WCAG 2.5.3) or speech input can't target what the button visibly says.
      aria-label={t(
        showEventBarFastFollows
          ? 'search.input.expandEventDetailsSearch'
          : 'search.input.expandEventDetails',
      )}
      className="flex h-full w-full items-center overflow-hidden rounded-full border bg-white shadow-event-bar-sm"
      data-testid="event-bar-v2-expand-trigger"
      onClick={onExpand}
      type="button"
    >
      <span className="min-w-0 flex-1 truncate px-5 text-left text-peppercorn-500">
        {whatValue || t('eventBarV2.what.menu.collapsedPlaceholder')}
      </span>
      <span
        aria-hidden
        className={twJoin(
          'mr-3 flex h-9 shrink-0 items-center justify-center rounded-full border-[1.5px]',
          'border-transparent bg-green-700 text-base font-bold text-white',
          showEventBarFastFollows ? 'w-auto px-6' : 'w-9',
        )}
        data-testid="event-bar-v2-expand-trigger-pill"
      >
        {showEventBarFastFollows ? (
          <span className="whitespace-nowrap">{t('eventBarV2.search')}</span>
        ) : (
          <Icon className="no-underline" icon={faMagnifyingGlass} size="xsmall" />
        )}
      </span>
    </button>
  );
};

export default EventBarV2CollapsedTrigger;
